import { Accordion, Box, Flex, FormLabel, Link } from "@chakra-ui/react";
import { ReactNode } from "react";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";

import { CodelistItem, CrsDetail, Dataset, RoleCode, RoleCodeFromJSON } from "@nens/rana/api-v1";
import { RanaHeading } from "@nens/ranalib";
import { RanaMarkdown } from "@nens/ranalib";

import { RanaAccordionItem } from "@/components/rana-accordion-item";
import { GoToExtractDatasetProcessFunc } from "@/lib/catalogue";

import { DatasetDetailExtract } from "./components/dataset-detail-extract";
import { DatasetDetailMap } from "./components/dataset-detail-map";
import { DatasetDetailMetadataTable } from "./components/dataset-detail-metadata-table";

// For now we only support Lizard and Nationaal Georegister as sources (#2491)
const determineDatasetSourceMetadata = ({
  resourceIdentifier,
  id,
  isHarvested,
}: Dataset): MetadataItems[number] | null => {
  const sourceLink = resourceIdentifier?.[0]?.link;
  const isLizardSource = typeof sourceLink === "string" ? sourceLink.includes("lizard") : false;

  if (isLizardSource) {
    return {
      label: <FormattedMessage id="label.lizard_raster" defaultMessage="Lizard raster" />,
      data: resourceIdentifier?.[0]?.code,
    };
  }

  if (isHarvested) {
    return {
      label: <FormattedMessage id="label.external_catalog" defaultMessage="External catalog" />,
      data: (
        <Link
          textDecor="underline"
          isExternal
          href={`https://www.nationaalgeoregister.nl/geonetwork/srv/dut/catalog.search#/metadata/${id}`}
        >
          <FormattedMessage
            id="catalogue.nationaal_georegister"
            defaultMessage="Nationaal Georegister"
          />
        </Link>
      ),
    };
  }

  return null;
};

const getPrettyRoleName = (role: string, intl: IntlShape) => {
  const prettyRoleNameMap: { [key in RoleCode]: string } = {
    resourceProvider: intl.formatMessage({
      id: "dataset.role_code.resourceProvider",
      defaultMessage: "Bronhouder",
    }),
    custodian: intl.formatMessage({
      id: "dataset.role_code.custodian",
      defaultMessage: "Beheerder",
    }),
    owner: intl.formatMessage({ id: "dataset.role_code.owner", defaultMessage: "Eigenaar" }),
    user: intl.formatMessage({ id: "dataset.role_code.user", defaultMessage: "Gebruiker" }),
    distributor: intl.formatMessage({
      id: "dataset.role_code.distributor",
      defaultMessage: "Distributeur",
    }),
    originator: intl.formatMessage({
      id: "dataset.role_code.originator",
      defaultMessage: "Opsteller",
    }),
    pointOfContact: intl.formatMessage({
      id: "dataset.role_code.pointOfContact",
      defaultMessage: "Contactpunt",
    }),
    principalInvestigator: intl.formatMessage({
      id: "dataset.role_code.principalInvestigator",
      defaultMessage: "Hoofdonderzoeker",
    }),
    processor: intl.formatMessage({
      id: "dataset.role_code.processor",
      defaultMessage: "Verwerker",
    }),
    publisher: intl.formatMessage({
      id: "dataset.role_code.publisher",
      defaultMessage: "Uitgever",
    }),
    author: intl.formatMessage({ id: "dataset.role_code.author", defaultMessage: "Auteur" }),
  };

  return role in prettyRoleNameMap ? prettyRoleNameMap[RoleCodeFromJSON(role)] : role;
};

const getContactMetadataItems = (
  intl: IntlShape,
  contact: Dataset["contact"]
): MetadataItems | undefined =>
  contact?.length
    ? [
        {
          label: (
            <FormattedMessage id="label.responsible_organisation" defaultMessage="Organisation" />
          ),
          data: contact.map(({ organisationObject }) => organisationObject),
        },
        {
          label: <FormattedMessage id="label.role_contact_person" defaultMessage="Role" />,
          data: contact.map(({ role }) => getPrettyRoleName(role, intl)),
        },
        {
          label: <FormattedMessage id="label.email_contact_person" defaultMessage="Email" />,
          data: contact.map(({ email }) => email),
        },
      ]
    : undefined;

interface DatasetDetailProps {
  dataset: Dataset;
  goToExtractDatasetProcess: GoToExtractDatasetProcessFunc;
}

export type DataItem = ReactNode | string | CodelistItem | CrsDetail | undefined | null | Date;

export type MetadataItems = {
  label: ReactNode;
  data: DataItem | DataItem[];
}[];

export const DatasetDetail = ({ goToExtractDatasetProcess, dataset }: DatasetDetailProps) => {
  const intl = useIntl();
  const {
    resourceTitleObject,
    resourceAbstractObject,
    clTopic,
    clStatus,
    clMaintenanceAndUpdateFrequency,
    resolutionScaleDenominator,
    mDConstraintsUseLimitationObject,
    lineageObject,
    resourceDate,
    licenseObject,
    crsDetails,
    contact,
    contactForResource,

    id,
    allKeywords,
  } = dataset;

  const metadataItems: MetadataItems = [
    {
      label: <FormattedMessage id="label.keywords" defaultMessage="Keywords" />,
      data: allKeywords,
    },
    {
      label: <FormattedMessage id="label.categories" defaultMessage="Categories" />,
      data: clTopic,
    },
    {
      label: <FormattedMessage id="label.status" defaultMessage="Status" />,
      data: clStatus ? clStatus[0] : undefined,
    },
    {
      label: <FormattedMessage id="label.revision_frequency" defaultMessage="Revision frequency" />,
      data: clMaintenanceAndUpdateFrequency ? clMaintenanceAndUpdateFrequency[0] : undefined,
    },
    {
      label: <FormattedMessage id="label.date_of_creation" defaultMessage="Date of creation" />,
      data: resourceDate?.map(({ type, date }) => (type === "creation" ? date : null)),
    },
    {
      label: (
        <FormattedMessage id="label.date_of_publication" defaultMessage="Date of publication" />
      ),
      data: resourceDate?.map(({ type, date }) => (type === "publication" ? date : null)),
    },
  ];

  const sourceMetadata = determineDatasetSourceMetadata(dataset);

  const technicalMetadataItems: MetadataItems = [
    {
      label: <FormattedMessage id="label.resolution" defaultMessage="Resolution" />,
      data: resolutionScaleDenominator
        ?.map((scaleDenominator) =>
          // Make sure the scale denominator is shown as "1:xxxx" if it's a number
          !isNaN(parseInt(scaleDenominator)) ? `1:${scaleDenominator}` : scaleDenominator
        )
        .join(", "),
    },
    {
      label: (
        <FormattedMessage
          id="label.coordinate_reference_system"
          defaultMessage="Coordinate reference system"
        />
      ),
      data: crsDetails,
    },
    {
      label: <FormattedMessage id="label.usage_limitations" defaultMessage="Usage limitations" />,
      data: mDConstraintsUseLimitationObject,
    },
    {
      label: <FormattedMessage id="label.license" defaultMessage="License" />,
      // Just render links here. Text is non sensical
      data: licenseObject?.map(({ link }) => link),
    },

    {
      label: (
        <FormattedMessage id="label.metadata_identifier" defaultMessage="Metadata identifier" />
      ),
      data: id,
    },

    ...(sourceMetadata ? [sourceMetadata] : []),
    {
      label: <FormattedMessage id="label.lineage" defaultMessage="Lineage" />,
      data: lineageObject,
    },
  ];

  const contactMetadataItems = getContactMetadataItems(intl, contact);
  const resourceContactMetadataItems = getContactMetadataItems(intl, contactForResource);

  return (
    <Flex h="full" flexDir="column" justifyContent="space-between">
      <Flex mb={4} alignItems="center" justifyContent="space-between">
        <RanaHeading type="h4">{resourceTitleObject._default}</RanaHeading>
        <DatasetDetailExtract
          goToExtractDatasetProcess={goToExtractDatasetProcess}
          dataset={dataset}
        />
      </Flex>

      <DatasetDetailMap dataset={dataset} />
      <Flex flexDirection={{ base: "column", lg: "row" }} mt={11} gap={11}>
        <Box w="full">
          <FormLabel mb={2} fontSize="l">
            <FormattedMessage id="label.description" defaultMessage="Description" />
          </FormLabel>
          {resourceAbstractObject?._default ? (
            <Box p={4} rounded="base" borderWidth="base" bg="brand.grey.100">
              <RanaMarkdown markdown={resourceAbstractObject?._default} />
            </Box>
          ) : (
            "-"
          )}
        </Box>
        <Box w="full">
          <DatasetDetailMetadataTable
            label={<FormattedMessage id="label.metadata" defaultMessage="Metadata" />}
            items={metadataItems}
            collapsableItems={technicalMetadataItems}
          />

          {(contactMetadataItems || resourceContactMetadataItems) && (
            <Accordion allowToggle mt={4}>
              <RanaAccordionItem
                title={intl.formatMessage({
                  id: "label.contact",
                  defaultMessage: "Contact",
                })}
              >
                {contactMetadataItems && (
                  <DatasetDetailMetadataTable
                    label={
                      <FormattedMessage
                        id="label.dataset_contact"
                        defaultMessage="Dataset contact"
                      />
                    }
                    items={contactMetadataItems}
                  />
                )}

                {resourceContactMetadataItems && (
                  <DatasetDetailMetadataTable
                    label={
                      <FormattedMessage
                        id="label.metadata_contact"
                        defaultMessage="Metadata contact"
                      />
                    }
                    items={resourceContactMetadataItems}
                  />
                )}
              </RanaAccordionItem>
            </Accordion>
          )}
        </Box>
      </Flex>
    </Flex>
  );
};
